import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import {
  getRosters,
  getWeeks,
  generateWeeks,
  updateWeek,
  saveRoster,
  importHistory,
  adjustHistory,
} from "../api/aquamanApi";

// All admin-page state and every write in one hook, so the components below it
// stay presentational.
const useAquamanAdmin = () => {
  const [rosters, setRosters] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedRosterId, setSelectedRosterId] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [fairness, setFairness] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const roster = useMemo(
    () => rosters.find((r) => r.dutyId === selectedRosterId) || null,
    [rosters, selectedRosterId]
  );

  const loadRosters = useCallback(async (keepSelection = true) => {
    const res = await getRosters();
    const list = res?.rosters || [];
    setRosters(list);
    setAvailableTeams(res?.availableTeams || []);
    setSelectedRosterId((prev) =>
      keepSelection && prev && list.some((r) => r.dutyId === prev)
        ? prev
        : list[0]?.dutyId || null
    );
    return list;
  }, []);

  const loadWeeks = useCallback(async (rosterId) => {
    if (!rosterId) {
      setWeeks([]);
      setFairness([]);
      return;
    }
    const res = await getWeeks({ rosterId });
    setWeeks(res?.weeks || []);
    setFairness(res?.fairness || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadRosters();
      } catch (err) {
        message.error("Nuk mund të lexohen rosterat e Aquaman.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadRosters]);

  useEffect(() => {
    if (!selectedRosterId) return;
    setLoading(true);
    loadWeeks(selectedRosterId)
      .catch(() => message.error("Nuk mund të lexohet kalendari."))
      .finally(() => setLoading(false));
  }, [selectedRosterId, loadWeeks]);

  // Any write refreshes both the roster row (served counts change) and the
  // timeline, so the panels can never disagree with the backend.
  const withRefresh = useCallback(
    async (fn, successMessage) => {
      setSaving(true);
      try {
        const result = await fn();
        await loadRosters();
        await loadWeeks(selectedRosterId);
        if (successMessage) message.success(successMessage);
        return result;
      } catch (err) {
        message.error(err?.response?.data?.error || "Veprimi dështoi.");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [loadRosters, loadWeeks, selectedRosterId]
  );

  const onSaveRoster = (patch, msg = "Rosteri u ruajt.") =>
    withRefresh(
      () =>
        saveRoster({
          rosterId: roster?.dutyId,
          dutyType: roster?.dutyType,
          team: roster?.team,
          ...patch,
        }),
      msg
    );

  const onCreateRoster = (team) =>
    withRefresh(
      () =>
        saveRoster({
          team,
          dutyType: "water",
          dutyLabel: "Aquaman",
          genderFilter: "Mashkull",
          fillFromTeam: true,
        }),
      `Rosteri për ${team} u krijua.`
    );

  const onGenerate = ({ weeks: count, dryRun }) =>
    withRefresh(async () => {
      const res = await generateWeeks({
        rosterId: selectedRosterId,
        weeks: count,
        dryRun,
      });
      setWarnings(res?.warnings || []);
      if (dryRun) setPreview(res?.weeks || []);
      else setPreview(null);
      return res;
    }, dryRun ? null : "Javët u gjeneruan.");

  const onWeekAction = (payload, msg) =>
    withRefresh(
      () => updateWeek({ rosterId: selectedRosterId, ...payload }),
      msg
    );

  const onImportHistory = ({ entries, dryRun }) =>
    withRefresh(async () => {
      const res = await importHistory({
        rosterId: selectedRosterId,
        entries,
        dryRun,
      });
      if (res?.unmatched?.length) {
        message.warning(
          `${res.unmatched.length} rreshta nuk u përputhën me punonjës.`
        );
      }
      return res;
    }, dryRun ? null : "Historiku u importua.");

  const onAdjustHistory = (adjustments) =>
    withRefresh(
      () => adjustHistory({ rosterId: selectedRosterId, adjustments }),
      "Historiku u përditësua."
    );

  return {
    rosters,
    roster,
    availableTeams,
    selectedRosterId,
    setSelectedRosterId,
    weeks,
    fairness,
    warnings,
    preview,
    clearPreview: () => setPreview(null),
    loading,
    saving,
    onSaveRoster,
    onCreateRoster,
    onGenerate,
    onWeekAction,
    onImportHistory,
    onAdjustHistory,
  };
};

export default useAquamanAdmin;
